import axios from "../api/axiosInstance";

function getRateLabel(rate) {
  const name = String(rate?.name || "").trim();
  const code = String(rate?.rate_code || "").trim();
  if (name && code) return `${name} (${code})`;
  return name || code || `Rate #${rate?.id}`;
}

function isDerivedRate(rate) {
  const baseRateId = Number(rate?.base_rate_id);
  return Number.isFinite(baseRateId) && baseRateId > 0;
}

function toDateMs(rawDate) {
  const iso = String(rawDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const ms = new Date(`${iso}T00:00:00`).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function getFilterStartMs(fromDate) {
  const explicit = toDateMs(fromDate);
  if (explicit != null) return explicit;

  // Default filter: only room rates with market data from yesterday onwards.
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - 1);
  return d.getTime();
}

function rowHasRecentCoverage(row, minDateMs) {
  const endMs = toDateMs(row?.end_date || row?.check_out);
  const startMs = toDateMs(row?.start_date || row?.check_in);

  if (endMs != null) return endMs >= minDateMs;
  if (startMs != null) return startMs >= minDateMs;
  return false;
}

export async function fetchMarketComparableRates({
  businessUnitId,
  includeDerived = false,
  fromDate = null,
}) {
  if (!businessUnitId) return [];
  const minDateMs = getFilterStartMs(fromDate);

  let rates = [];
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/roomRates/all`, {
      params: { business_unit_id: Number(businessUnitId) },
    });
    rates = Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    console.error("[MarketComparableRates] Failed to load room rates:", err);
    return [];
  }

  const candidates = rates.filter((rate) => {
    if (!rate?.id) return false;
    if (!includeDerived && isDerivedRate(rate)) return false;
    return true;
  });

  const out = [];
  const batchSize = 6;

  for (let i = 0; i < candidates.length; i += batchSize) {
    const batch = candidates.slice(i, i + batchSize);
    const results = await Promise.all(
      batch.map(async (rate) => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/market-rate-prices/${rate.id}`
          );
          const rows = Array.isArray(res?.data) ? res.data : [];
          if (!rows.length) return null;

          const recentRows = rows.filter((row) => rowHasRecentCoverage(row, minDateMs));
          if (!recentRows.length) return null;

          return {
            id: Number(rate.id),
            label: getRateLabel(rate),
            rate,
            marketRows: recentRows.length,
          };
        } catch (err) {
          return null;
        }
      })
    );

    out.push(...results.filter(Boolean));
  }

  return out.sort((a, b) => String(a.label).localeCompare(String(b.label)));
}
